// SPDX-FileCopyrightText: 2026 Povl Filip Sonne-Frederiksen
//
// SPDX-License-Identifier: GPL-3.0-or-later

/**
 * Konto as data: what each self-service change sends, the checks before it
 * is sent, and the Danish line for a refusal. Pure, so it is testable
 * without a DOM.
 *
 * Supabase owns accounts (ruxd-wmc.5): the browser changes its own name,
 * e-mail and password with supabase-js, never through ruxd. A password change
 * re-proves the current password first (a fresh sign-in), so a borrowed,
 * still-open browser cannot lock its owner out.
 */

import { SupabaseAuthError } from '../api/supabase';
import { normalizeMemberEmail } from '../app/auth';

export type Check<T> = { ok: true; value: T } | { ok: false; reason: string };

/** GoTrue's default minimum is 6; ReUseX asks for 8. */
export const MIN_PASSWORD_LENGTH = 8;
/** ruxd's limit for `user_metadata.username`: 64 bytes of UTF-8 (æøå count twice). */
export const MAX_USERNAME_BYTES = 64;

/** The display name (`user_metadata.username`); empty clears it, so the e-mail shows. */
export function usernameCheck(raw: string, current: string): Check<string> {
  const value = raw.trim();
  if (value === current.trim()) return { ok: false, reason: 'Navnet er uændret.' };
  if (/[\u0000-\u001f\u007f]/.test(value)) return { ok: false, reason: 'Navnet må ikke indeholde kontroltegn.' };
  if (new TextEncoder().encode(value).length > MAX_USERNAME_BYTES)
    return { ok: false, reason: 'Navnet er for langt (højst 64 bytes; æ, ø og å tæller dobbelt).' };
  return { ok: true, value };
}

export function emailChangeCheck(raw: string, current: string): Check<string> {
  const value = normalizeMemberEmail(raw);
  if (value === null) return { ok: false, reason: 'Skriv en gyldig e-mail.' };
  if (value === current.trim().toLowerCase()) return { ok: false, reason: 'Det er din nuværende e-mail.' };
  return { ok: true, value };
}

export interface PasswordDraft {
  current: string;
  next: string;
  repeat: string;
}

export function passwordChangeCheck(d: PasswordDraft): Check<string> {
  if (d.current === '') return { ok: false, reason: 'Skriv din nuværende adgangskode.' };
  if (d.next.length < MIN_PASSWORD_LENGTH)
    return { ok: false, reason: `Den nye adgangskode skal være mindst ${MIN_PASSWORD_LENGTH} tegn.` };
  if (d.next !== d.repeat) return { ok: false, reason: 'De to nye adgangskoder er ikke ens.' };
  if (d.next === d.current) return { ok: false, reason: 'Den nye adgangskode skal være en anden end den nuværende.' };
  return { ok: true, value: d.next };
}

/** The Danish line under a refused account change, from GoTrue's error code. */
export function accountErrorText(cause: unknown): string {
  if (cause instanceof SupabaseAuthError) {
    switch (cause.code) {
      case 'invalid_credentials':
        return 'Den nuværende adgangskode er forkert.';
      case 'weak_password':
        return 'Adgangskoden er for svag. Vælg en længere eller mindre forudsigelig.';
      case 'same_password':
        return 'Den nye adgangskode skal være en anden end den nuværende.';
      case 'email_exists':
      case 'email_address_not_authorized':
        return cause.code === 'email_exists'
          ? 'Der findes allerede en bruger med den e-mail.'
          : 'Den e-mail kan ikke bruges her.';
      case 'over_email_send_rate_limit':
      case 'over_request_rate_limit':
        return 'For mange forsøg. Vent lidt, og prøv igen.';
      case 'session_not_found':
      case 'reauthentication_needed':
        return 'Log ind igen, og prøv så igen.';
    }
    if (cause.status === 429) return 'For mange forsøg. Vent lidt, og prøv igen.';
  }
  return 'Ændringen kunne ikke gemmes.';
}
